import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { Page } from '../domain/page.model';
import { HttpUtils } from '../utils/http-utils';
import { Domain, DomainListRequest } from '../domain/domain.model';
import {
  DomainDpComplaince,
  DpBundleCompliance,
  DpBundleComplianceRequest,
  DpComplianceStatus,
  DPDomainCompliance,
} from '../domain/demand-partner-compliance.model';
import { first, map } from 'rxjs/operators';
import { PublisherDomainNew } from '../domain/publisher-domain-new.model';

export interface CreateUpdateDomainInput {
  publisherId: string;
  domainId?: string;
  publisherDomain: {
    url: PublisherDomainNew['domain']['url'];
    name: PublisherDomainNew['domain']['name'];
    adsTxtLocation: PublisherDomainNew['adsTxtLocation'];
    keywords: PublisherDomainNew['keywords'];
  };
}

@Injectable({
  providedIn: 'root',
})
export class DomainsService {
  constructor(private httpClient: HttpClient) {}

  bundleCompliance(
    filter: DpBundleComplianceRequest
  ): Observable<Page<DpBundleCompliance>> {
    return this.httpClient.get<Page<DpBundleCompliance>>(
      `${environment.serviceUrl}/api/dp/compliance`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  domainsCompliance(filter: any): Observable<Page<DPDomainCompliance>> {
    return this.httpClient.get<Page<DPDomainCompliance>>(
      `${environment.serviceUrl}/api/dp/domain-compliance`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  countByStatus() {
    return this.httpClient.get(
      `${environment.serviceUrl}/api/domain/dp/approval/count-by-status`
    );
  }

  updateDpCompliance(dpCompliance: DpBundleCompliance) {
    return this.httpClient.put(
      `${environment.serviceUrl}/api/domain/dp/approval/${dpCompliance.id}`,
      dpCompliance
    );
  }

  complianceDpStatuses(id: string): Observable<DomainDpComplaince[]> {
    return this.httpClient.get<DomainDpComplaince[]>(
      `${environment.serviceUrl}/api/dp/compliance/${id}`
    );
  }

  complianceDomainDpStatuses(id: string): Observable<DomainDpComplaince[]> {
    return this.httpClient.get<DomainDpComplaince[]>(
      `${environment.serviceUrl}/api/dp/domain-compliance/${id}`
    );
  }

  updateDpComplianceStatus(id: string, status: DpComplianceStatus) {
    return this.httpClient.put(
      `${environment.serviceUrl}/api/domain/dp/approval/${id}`,
      { status }
    );
  }

  updateDemandPartners(dp: DomainDpComplaince[], approvalId: string) {
    return this.httpClient.post(
      `${environment.serviceUrl}/api/domain/dp/approval/${approvalId}/status/batch`,
      dp
    );
  }

  updateDpApproval(dp: DomainDpComplaince[]) {
    return this.httpClient.put(`${environment.serviceUrl}/api/dp/approvals`, {
      approvals: dp,
    });
  }

  updateDpDomainApproval(dp: DomainDpComplaince[]) {
    return this.httpClient.put(
      `${environment.serviceUrl}/api/dp/domain-approvals`,
      { approvals: dp }
    );
  }

  updateAdsTxtSent(adsTxtSent: boolean, id: string) {
    return this.httpClient.put(`${environment.serviceUrl}/api/domain/${id}`, {
      adsTxtSent,
    });
  }

  getAllDomains(filter: any): Observable<Page<Domain>> {
    return this.httpClient.get<Page<Domain>>(`${environment.serviceUrl}/api/domains`, {
      params: HttpUtils.getHttpParams(filter)
    });
  }

  getAllPubDomains(filter: any): Observable<Page<Domain>> {
    return this.httpClient.get<Page<Domain>>(`${environment.serviceUrl}/api/publishers/report/domains`, {
      params: HttpUtils.getHttpParams(filter)
    });
  }

  exportAllDomainsCSV(publisherId: string): Observable<string> {
    return this.httpClient
      .get<string>(
        `${environment.serviceUrl}/api/publishers/${publisherId}/domains/csv/export`,
        {
          responseType: 'blob',
          header: {
            Accept: 'text/csv',
          },
        } as any
      )
      .pipe(
        map((response: any) => {
          const blob = new Blob([response], {
            type: 'text/csv',
          });
          return URL.createObjectURL(blob);
        })
      );
  }

  updateDomainsCSV(publisherId: string, file: File): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    return this.httpClient
      .post<any>(
        `${environment.serviceUrl}/api/publishers/${publisherId}/domains/csv/import-update`,
        formData
      )
      .pipe(first());
  }

  addDomainsCSV(publisherId: string, file: File): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    return this.httpClient
      .post<any>(
        `${environment.serviceUrl}/api/publishers/${publisherId}/domains/csv/import-add`,
        formData
      )
      .pipe(first());
  }

  createDomain({ publisherId, publisherDomain }: CreateUpdateDomainInput) {
    console.log('createDomain:::', publisherDomain);
    return this.httpClient.post(`${environment.serviceUrl}/api/publishers/${publisherId}/domains`, publisherDomain);
  }

  updateDomain({ publisherId, domainId, publisherDomain }: CreateUpdateDomainInput) {
    console.log('updateDomain:::', publisherDomain);
    return this.httpClient.put(`${environment.serviceUrl}/api/publishers/${publisherId}/domains/${domainId}`, publisherDomain);
  }

  downloadTemplateCsv(publisherId: string): Observable<string> {
    return this.httpClient
      .get<string>(
        `${environment.serviceUrl}/api/publishers/${publisherId}/domains/download/csv-template/insert-new-domains`,
        {
          responseType: 'blob',
          header: {
            Accept: 'text/csv',
          },
        } as any
      )
      .pipe(
        map((response: any) => {
          const blob = new Blob([response], {
            type: 'text/csv',
          });
          return URL.createObjectURL(blob);
        })
      );
  }
}
